import * as React from 'react';
import * as classNames from 'classnames';
import {withStyles, WithStyles} from '@material-ui/core/styles';
import Dialog, {DialogProps} from '@material-ui/core/Dialog/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Popover, {PopoverProps} from '@material-ui/core/Popover';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import {Omit} from '@material-ui/core';
import * as _ from 'lodash';
import createStyles from '@material-ui/core/styles/createStyles';
import {Theme} from '@material-ui/core/styles/createMuiTheme';

export interface BasicDialogProps extends Omit<DialogProps, 'title' | 'children' | 'ref' | 'onClose'> {
  title?: React.ReactNode;
  content?: React.ReactNode;
  text?: React.ReactNode;
  cancel?: boolean;
  actions?: React.ReactElement<any>;
  TitleComponent?: typeof DialogTitle;
  ContentComponent?: typeof DialogContent;
  ContentTextComponent?: typeof DialogContentText;
  ActionsComponent?: typeof DialogActions;
  anchorEl?: PopoverProps['anchorEl'];
  variant?: 'announcement';
  onClose?: any;
}

const styles = (theme: Theme) =>
  createStyles({
    root: {
      // flex is required to align Popover behavior with Dialog.
      display: 'flex',
      flexDirection: 'column',
    },
    closeButton: {
      position: 'absolute',
      top: 0,
      right: 0,
    },
    announcement: {
      display: 'flex',
      alignItems: 'center',
      '& img': {
        maxWidth: '70%',
        height: 350,
        padding: theme.spacing(2),
        maxHeight: 'calc(100vh - 350px)',
      },
      '& $contentText': {
        textAlign: 'center',
      },
      '& $actionsContainer': {
        alignItems: 'baseline',
        marginBottom: theme.spacing(3),
      },
      '& $actionsContainer > * + *': {
        marginLeft: theme.spacing(2),
      },
      '& $contentContainer': {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: 0,
      },
    },
    titleWithCancel: {
      paddingRight: theme.spacing(5),
    },
    contentText: {},
    contentContainer: {},
    actionsContainer: {},
  });

const BasicDialog: React.FunctionComponent<BasicDialogProps & WithStyles<typeof styles>> = ({
  title,
  content,
  text,
  cancel,
  actions,
  TitleComponent,
  ContentComponent,
  ContentTextComponent,
  ActionsComponent,
  classes: {
    root,
    titleWithCancel,
    actionsContainer,
    contentContainer,
    contentText,
    announcement,
    closeButton,
    ...otherClasses
  },
  ...props
}) => {
  const {onClose, anchorEl, variant, open} = props;
  const disabled = !onClose;
  const Component = anchorEl ? Popover : Dialog;
  return (
    <Component
      open={open}
      classes={{
        ...otherClasses,
        paper: classNames({[root]: true, [otherClasses.paper]: true, [announcement]: variant === 'announcement'}),
      }}
      {..._.omitBy(anchorEl ? _.omit(props, ['maxWidth', 'fullWidth']) : props, value => value === undefined)}
    >
      <TitleComponent className={classNames({[titleWithCancel]: cancel})}>{title}</TitleComponent>
      <ContentComponent className={contentContainer}>
        <ContentTextComponent className={contentText}>{text}</ContentTextComponent>
        {_.isFunction(content) ? content(props) : content}
      </ContentComponent>
      {actions ? (
        <ActionsComponent className={actionsContainer}>
          {cancel ? (
            <Button className={'cla-dialog-button-cancel'} disabled={disabled} cancel onClick={onClose as any}>
              Cancel
            </Button>
          ) : (
            undefined
          )}
          {_.isFunction(actions) ? actions(props) : actions}
        </ActionsComponent>
      ) : (
        undefined
      )}
      {cancel ? (
        <IconButton
          className={'cla-dialog-button-x'}
          disabled={disabled}
          onClick={onClose as any}
          classes={{root: closeButton}}
        >
          <ClearIcon />
        </IconButton>
      ) : (
        undefined
      )}
    </Component>
  );
};

BasicDialog.defaultProps = {
  TitleComponent: DialogTitle,
  ContentComponent: DialogContent,
  ContentTextComponent: DialogContentText,
  ActionsComponent: DialogActions,
  maxWidth: 'md',
};
export default withStyles(styles)(BasicDialog) as React.FunctionComponent<BasicDialogProps>;
